import { reduceReducers } from '@shared/util/reduce-reducers'
import { Action, combineReducers, createReducer, on } from '@ngrx/store'
import {
  GseStructureGroupEntity,
  GseStructureGroupMemberEntity,
} from '../../model/gse-structure-group.model'
import { CreditCalculationStructure } from '../../model/credit-structure.model'
import { isDefined } from '../../utils/credit.utils'
import { DEFAULT_FILTER, ProgramsYearsFilter } from './../../../tier/credit/credit-tier.model'
import * as fromGseGroupActions from './gse-group.actions'
import { GseStructureGroupFilterActions } from './gse-group.actions'

export interface State {
  filters: ProgramsYearsFilter
  currentGseStructureGroupEntity?: GseStructureGroupEntity
  currentGseStructureGroupMemberEntities?: GseStructureGroupMemberEntity[]
  removedMemberIds: number[]
  isDirty: boolean
  isSaving: boolean
}

export const initialState: State = {
  isDirty: false,
  isSaving: false,
  removedMemberIds: [],
  filters: DEFAULT_FILTER,
}

const filtersReducer = createReducer(
  initialState.filters,
  on(GseStructureGroupFilterActions.update, (_, { filters }) => filters),
  on(GseStructureGroupFilterActions.reset, () => DEFAULT_FILTER),
)

const currentGseStructureGroupEntityReducer = createReducer(
  initialState.currentGseStructureGroupEntity,
  on(
    fromGseGroupActions.AddGseStructureGroupSuccess,
    fromGseGroupActions.SaveGseStructureGroupSuccess,
    (_, { gseStructureGroupEntity }) => gseStructureGroupEntity,
  ),
  on(
    fromGseGroupActions.UpdateGseStructureGroupSuccess,
    (state, { gseStructureGroupMemberEntity }) => {
      if (!state) {
        return state
      }
      const groupTransactions = state.creditCalculationStructure.transactions
      return {
        ...state,
        creditCalculationStructure: {
          ...state.creditCalculationStructure,
          transactions: [
            ...groupTransactions,
            ...gseStructureGroupMemberEntity.creditCalculationStructure.transactions,
          ],
        },
      }
    },
  ),
  on(fromGseGroupActions.RenameGseStructureGroup, (state, { label }) => {
    if (!state) {
      return state
    }
    return {
      ...state,
      gseStructureGroup: {
        ...state.gseStructureGroup,
        label,
      },
      creditCalculationStructure: {
        ...state.creditCalculationStructure,
        name: label,
      },
    }
  }),
  on(
    fromGseGroupActions.RemoveGseStructureGroup,
    fromGseGroupActions.DeleteGseStructureGroupSuccess,
    GseStructureGroupFilterActions.reset,
    (_) => undefined,
  ),
  on(fromGseGroupActions.RemoveGseStructureFromGroup, (state, { creditCalculationStructure }) => {
    if (!state) {
      return state
    }
    const updatedCreditCalculationStructure: CreditCalculationStructure = {
      ...state.creditCalculationStructure,
      transactions: state.creditCalculationStructure.transactions.filter(
        (transaction) =>
          !creditCalculationStructure.transactions.some(
            (transactionToRemove) => transactionToRemove._id === transaction._id,
          ),
      ),
    }
    return {
      ...state,
      creditCalculationStructure: updatedCreditCalculationStructure,
    }
  }),
  on(
    fromGseGroupActions.FetchGseCalculationStructureForGroupSuccess,
    (_, { gseStructureGroup, creditCalculationStructure }) => {
      return {
        gseStructureGroup,
        creditCalculationStructure,
      }
    },
  ),
)

const currentGseStructureGroupMemberEntitiesReducer = createReducer(
  initialState.currentGseStructureGroupMemberEntities,
  on(fromGseGroupActions.AddGseStructureGroupSuccess, (_, { gseStructureGroupMemberEntity }) => [
    gseStructureGroupMemberEntity,
  ]),
  on(
    fromGseGroupActions.UpdateGseStructureGroupSuccess,
    (state, { gseStructureGroupMemberEntity }) => {
      if (!state) {
        return state
      }
      return [...state, gseStructureGroupMemberEntity]
    },
  ),
  on(
    fromGseGroupActions.SaveGseStructureGroupMembersSuccess,
    (state, { gseStructureGroupMembers }) => {
      if (!state) {
        return state
      }
      const updatedMembers: GseStructureGroupMemberEntity[] = state.map((member) => {
        const found = gseStructureGroupMembers.find(
          (m) => m.gse_structure_id === member.gseStructureGroupMember.gse_structure_id,
        )
        return found
          ? {
              ...member,
              gseStructureGroupMember: found,
            }
          : member
      })
      return updatedMembers
    },
  ),
  on(
    fromGseGroupActions.RemoveGseStructureGroup,
    fromGseGroupActions.DeleteGseStructureGroupSuccess,
    GseStructureGroupFilterActions.reset,
    (_) => [],
  ),
  on(fromGseGroupActions.RemoveGseStructureFromGroup, (state, { creditCalculationStructure }) => {
    if (!state) {
      return state
    }
    return state.filter(
      (member) => member.creditCalculationStructure._id !== creditCalculationStructure._id,
    )
  }),
  on(
    fromGseGroupActions.FetchGseGroupMemberCalculationStructuresSuccess,
    (_, { gseStructureGroupMembers, gseStructures, creditCalculationStructures }) => {
      const gseStructureGroupMemberEntities: GseStructureGroupMemberEntity[] =
        gseStructureGroupMembers
          .map((gseStructureGroupMember) => {
            const gseStructure = gseStructures.find(
              (s) => s.id === gseStructureGroupMember.gse_structure_id,
            )
            const creditCalculationStructure = creditCalculationStructures.find(
              (cs) => cs._id === gseStructure?.gse_calculation_structure_id,
            )
            if (gseStructure && creditCalculationStructure) {
              return {
                gseStructureGroupMember,
                gseStructure,
                creditCalculationStructure,
              }
            }
          })
          .filter(isDefined)
      return gseStructureGroupMemberEntities
    },
  ),
)

const isDirtyReducer = createReducer(
  initialState.isDirty,
  on(
    fromGseGroupActions.AddGseStructureGroup,
    fromGseGroupActions.UpdateGseStructureGroup,
    fromGseGroupActions.RenameGseStructureGroup,
    fromGseGroupActions.RemoveGseStructureFromGroup,
    () => true,
  ),
  on(
    fromGseGroupActions.SaveGseStructureGroupMembersSuccess,
    fromGseGroupActions.RemoveGseStructureGroup,
    GseStructureGroupFilterActions.reset,
    () => false,
  ),
)

const isSavingReducer = createReducer(
  initialState.isSaving,
  on(fromGseGroupActions.SaveGseStructureGroup, () => true),
  on(
    fromGseGroupActions.SaveGseStructureGroupMembersSuccess,
    fromGseGroupActions.SaveGseStructureGroupFailure,
    fromGseGroupActions.SaveGseStructureGroupMembersFailure,
    () => false,
  ),
)

const removedMemberIdsReducer = createReducer(
  initialState.removedMemberIds,
  on(fromGseGroupActions.RemoveGseStructureFromGroup, (state, { gseStructureGroupMemberId }) =>
    gseStructureGroupMemberId ? [...state, gseStructureGroupMemberId] : state,
  ),
  on(
    fromGseGroupActions.RemoveGseStructureGroup,
    fromGseGroupActions.DeleteGseStructureGroupSuccess,
    fromGseGroupActions.SaveGseStructureGroupMembersSuccess,
    GseStructureGroupFilterActions.reset,
    () => [],
  ),
)

const gseGroupReducer = combineReducers<State>({
  filters: filtersReducer,
  currentGseStructureGroupEntity: currentGseStructureGroupEntityReducer,
  currentGseStructureGroupMemberEntities: currentGseStructureGroupMemberEntitiesReducer,
  removedMemberIds: removedMemberIdsReducer,
  isDirty: isDirtyReducer,
  isSaving: isSavingReducer,
})

const reducedReducer = reduceReducers(gseGroupReducer)

export function reducer(state: State | undefined, action: Action): State {
  return reducedReducer(state, action)
}
